import { Component } from 'react'
import { C, glassStyle } from './theme'
import { Sentry } from './sentry'
import { Icon } from './components/Icon'

// ── App-level Error Boundary ────────────────────────────────────────────
export class AppErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error Boundary caught:', error, errorInfo)
    Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#FAFAF7', fontFamily: "'Outfit', sans-serif", padding: 24,
        }}>
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center', color: '#C73428' }}>
              <Icon name="alert-triangle" size={48} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A17', marginBottom: 8 }}>
              Something went wrong
            </h2>
            <p style={{ color: '#8C8C8C', fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>
              Settle ran into an unexpected error. Your data is safe — try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#0F6E56', color: '#fff', border: 'none', borderRadius: 14,
                padding: '14px 32px', fontSize: 16, fontWeight: 700, cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
              }}
            >
              Refresh Settle
            </button>
            {this.state.error && (
              <details style={{ marginTop: 20, textAlign: 'left', fontSize: 12, color: '#8C8C8C' }}>
                <summary style={{ cursor: 'pointer', marginBottom: 8 }}>Error details</summary>
                <pre style={{
                  background: '#F7F7F5', padding: 12, borderRadius: 8, overflow: 'auto',
                  fontSize: 11, lineHeight: 1.5, maxHeight: 200,
                }}>
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// ── Per-feature Error Boundary ──────────────────────────────────────────
export class FeatureErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error(`Feature Error [${this.props.name || 'unknown'}]:`, error, errorInfo)
    Sentry.captureException(error, {
      tags: { feature: this.props.name || 'unknown' },
      extra: { componentStack: errorInfo?.componentStack },
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          ...glassStyle({ strong: true }),
          borderRadius: 20,
          padding: 24, textAlign: 'center', margin: '16px 0',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: 18,
            background: `linear-gradient(135deg, ${C.danger}22 0%, ${C.danger}10 100%)`,
            border: `1px solid ${C.danger}33`,
            backdropFilter: 'blur(14px) saturate(160%)',
            WebkitBackdropFilter: 'blur(14px) saturate(160%)',
            color: C.danger,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Icon name="alert-triangle" size={28} />
          </div>
          <div style={{
            fontFamily: C.serif, fontWeight: 400, fontSize: 22,
            color: C.text, marginBottom: 6, letterSpacing: -0.3,
          }}>
            {this.props.name || 'This section'} hit a <span style={{ fontStyle: 'italic' }}>snag</span>
          </div>
          <div style={{ color: C.muted, fontSize: 13, marginBottom: 18, lineHeight: 1.5 }}>
            Your other features still work fine.
          </div>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentMid} 100%)`,
              color: '#fff', border: 'none', borderRadius: 999,
              padding: '10px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Outfit', sans-serif",
              boxShadow: '0 8px 22px rgba(15,110,86,0.28)',
            }}
          >
            Try again
          </button>
          {this.state.error && (
            <details style={{ marginTop: 16, textAlign: 'left', fontSize: 12, color: C.muted }}>
              <summary style={{ cursor: 'pointer', marginBottom: 8 }}>Error details</summary>
              <pre style={{
                background: C.glass,
                border: `1px solid ${C.glassBorder}`,
                backdropFilter: 'blur(14px) saturate(160%)',
                WebkitBackdropFilter: 'blur(14px) saturate(160%)',
                padding: 12, borderRadius: 12, overflow: 'auto',
                fontSize: 11, lineHeight: 1.5, maxHeight: 240, whiteSpace: 'pre-wrap',
                wordBreak: 'break-word', color: C.text,
              }}>
                {this.state.error.toString()}
                {this.state.error?.stack ? `\n\n${this.state.error.stack}` : ''}
              </pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
